import { useAtom, useAtomValue } from 'jotai';
import { ToggleItemVisibility } from '../ToggleItemVisibility';
import * as state from '@/components/Analysis/modules/pointOfInterest/state';
import { useEffect, useMemo } from 'react';
import {
  selectedSpherical as selectedSphericalAtom,
  voiVisibilityBox as voiVisibilityBoxAtom,
} from '@/components/Analysis/state';
import { convertCuboidToAdjustedVisibilityBoxProps } from '@/utils/geometry';
import { CuboidBase } from '@/modules/cuboids/types';
import queryString from 'query-string';
import { updateUrl } from '@/utils';
import {
  currentCuboid as currentCuboidAtom,
  cuboids as cuboidsAtom,
} from '@/components/Analysis/modules/cuboids';

export const IsolateVoiPointCloud = () => {
  const selectedPointOfInterest = useAtomValue(state.selectedPointOfInterest);
  const selectedSpherical = useAtomValue(selectedSphericalAtom);
  const currentCuboid = useAtomValue(currentCuboidAtom);
  const cuboids = useAtomValue(cuboidsAtom);
  const [voiVisibilityBox, setVoiVisibilityBox] = useAtom(voiVisibilityBoxAtom);

  const cuboid: CuboidBase | undefined = useMemo(() => {
    if (currentCuboid) {
      // Use the top-level position/rotation/scale from the styled cuboid, not cuboidData,
      // because CuboidEditor updates those live without updating cuboidData.
      const { position, rotation, scale } = currentCuboid;
      return { ...currentCuboid.cuboidData, position, rotation, scale } as CuboidBase;
    }

    if (selectedPointOfInterest?.cuboids?.[0]) {
      // Prefer the cuboids atom over selectedPointOfInterest because selectedPointOfInterest
      // is only updated on initial query load (onCompleted doesn't re-fire after refetch),
      // while cuboids is kept fresh by initializeCuboids after every save.
      const cuboidId = selectedPointOfInterest.cuboids[0].id;
      return (cuboids.get(cuboidId) ?? selectedPointOfInterest.cuboids[0]) as CuboidBase;
    }

    return undefined;
  }, [currentCuboid, selectedPointOfInterest?.cuboids, cuboids]);
  const checked = !!voiVisibilityBox;

  const disabled = useMemo(() => {
    // If user's not in spherical view
    if (!selectedSpherical) return true;
    // If no POI is selected, toggle should be disabled
    if (!cuboid) return true;
    return false;
  }, [cuboid, selectedSpherical]);

  // EDGE CASE:
  // When user is in spherical view and has the toggle turned on,
  // then they unselect the POI and then get out of the shperical
  // now the toggle is disabled and the visibility is set to the cuboid's view box
  // so we reset the visibility box to default whenever the selectedPointOfInterest is undefined
  useEffect(() => {
    if (!selectedPointOfInterest) {
      setVoiVisibilityBox(undefined);
    }
  }, [selectedPointOfInterest, setVoiVisibilityBox]);

  // EDGE CASE:
  // When user has the toggle on, and is in spherical view
  // then they get out of spherical view
  // now the toggle is disabled and the visibility is set to the cuboid's view box
  // so we reset the visibility box to default whenever the selectedPointOfInterest is undefined
  useEffect(() => {
    if (!selectedSpherical) {
      setVoiVisibilityBox(undefined);
    }
  }, [selectedSpherical, setVoiVisibilityBox]);

  const onToggle = () => {
    if (!cuboid) return;
    if (!checked) {
      const target = convertCuboidToAdjustedVisibilityBoxProps(cuboid);
      setVoiVisibilityBox(target);
      updateUrl('voiVisibility', encodeURIComponent(JSON.stringify({ value: target })));
    } else {
      setVoiVisibilityBox(undefined);
    }
  };

  useEffect(() => {
    const query = window.location.search;
    const parsed = queryString.parse(query, { arrayFormat: 'comma' });
    if (parsed.voiVisibility) {
      const voiVisibility = JSON.parse(decodeURIComponent(parsed.voiVisibility.toString()));
      setVoiVisibilityBox(voiVisibility.value);
    }
  }, [setVoiVisibilityBox]);

  return (
    <>
      <ToggleItemVisibility
        label="Isolate VOI Point Cloud"
        disabled={disabled}
        isVisible={checked}
        toggleVisibility={onToggle}
      />
    </>
  );
};
